import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";
import Dropdown from "../components/Dropdown.jsx";
import Pagination from "../components/Pagination.jsx";
import "../styles/Officers.css";

export default function Officers() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [officers, setOfficers] = useState([]);
  const [zones, setZones] = useState([]);
  const [show, setShow] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const PAGE_SIZE = 10;
  const [search, setSearch] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");

  // A zone login adds officers only to its own zone — no zone picker.
  const zoneLocked = user.role === "ZONAL_AC" && !!user.zone;
  const blankForm = {
    mobile: "", full_name: "", role: "OFFICER", designation: "",
    department: "",
    zone: zoneLocked ? user.zone : "", password: "audit@123",
  };
  const [form, setForm] = useState(blankForm);
  const [error, setError] = useState("");
  const [departments, setDepartments] = useState([]);

  const canAdd = ["SUPER_ADMIN", "CORP_ADMIN", "ZONAL_AC"].includes(user.role);

  const load = useCallback(() => {
    const params = { page_size: PAGE_SIZE, page };
    if (search.trim()) params.search = search.trim();
    if (zoneFilter) params.zone = zoneFilter;
    api.get("/auth/users/", { params })
      .then((r) => {
        setOfficers(r.data.results || r.data);
        setCount(r.data.count ?? (r.data.results || r.data).length);
      })
      .catch((err) => {
        console.error("Failed to load officers:", err);
      });
  }, [page, search, zoneFilter]);

  useEffect(() => {
    api.get("/zones/")
      .then((r) => setZones(r.data.results || r.data))
      .catch((err) => console.error("Failed to load zones:", err));
    api.get("/departments/")
      .then((r) => setDepartments(r.data.results || r.data))
      .catch((err) => console.error("Failed to load departments:", err));
  }, []);

  useEffect(() => {
    const id = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(id);
  }, [load, search, zoneFilter]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setError("");
    try {
      const payload = { ...form };
      if (!payload.zone) delete payload.zone;
      await api.post("/auth/users/", payload);
      setShow(false);
      setForm(blankForm);
      if (page === 1) {
        load();
      } else {
        setPage(1);
      }
    } catch (e) {
      setError(JSON.stringify(e.response?.data || "Failed."));
    }
  };

  return (
    <div className="page-officers">
      <div className="toolbar">
        <div className="search-wrap">
          <i className="ti ti-search"></i>
          <input
            placeholder={t("search")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        {user.role !== "ZONAL_AC" && (
          <Dropdown
            value={zoneFilter}
            onChange={(v) => {
              setZoneFilter(v);
              setPage(1);
            }}
            placeholder={t("filterZone")}
            options={[
              { value: "", label: t("filterZone") },
              ...zones.map((z) => ({ value: String(z.id), label: z.name })),
            ]}
          />
        )}
        {(search || zoneFilter) && (
          <button
            className="btn btn-outline"
            onClick={() => {
              setSearch("");
              setZoneFilter("");
              setPage(1);
            }}
            style={{ height: "33px", padding: "0 12px" }}
          >
            Clear
          </button>
        )}
        <div className="spacer" />
        {canAdd && (
          <button className="btn btn-primary" onClick={() => setShow(true)}>
            + {t("addUser")}
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t("fullName")}</th>
                <th>{t("mobile")}</th>
                <th>{t("role")}</th>
                <th>{t("designation")}</th>
                <th>{t("department")}</th>
                <th>{t("zone")}</th>
              </tr>
            </thead>
            <tbody>
              {officers.map((o) => (
                <tr 
                  key={o.id}
                  onClick={() => navigate(`/officers/${o.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <strong>{o.full_name}</strong>
                  </td>
                  <td>{o.mobile}</td>
                  <td>
                    <span className="tag-pill">{o.role_display}</span>
                  </td>
                  <td>{o.designation || "—"}</td>
                  <td>{o.department || "—"}</td>
                  <td>{o.zone_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        page={page}
        totalPages={Math.ceil(count / PAGE_SIZE)}
        onChange={setPage}
        leftSlot={<span>{t("totalUsers")}: <strong>{count}</strong></span>}
      />

      {show && createPortal(
        <div className="modal-overlay" onClick={() => setShow(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t("addUser")}</h3>
            {error && <div className="error-text">{error}</div>}
            <div className="row">
              <div className="field">
                <label>{t("fullName")}</label>
                <input value={form.full_name} onChange={set("full_name")} />
              </div>
              <div className="field">
                <label>{t("mobile")}</label>
                <input value={form.mobile} onChange={set("mobile")} />
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>{t("zone")}</label>
                {zoneLocked ? (
                  <input value={user.zone_name || ""} disabled />
                ) : (
                  <select value={form.zone} onChange={set("zone")}>
                    <option value="">Select Zone</option>
                    {zones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>{t("designation")}</label>
                <input value={form.designation} onChange={set("designation")} />
              </div>
              <div className="field">
                <label>{t("department")}</label>
                <select value={form.department} onChange={set("department")}>
                  <option value="">Select Department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>{t("password")}</label>
                <input value={form.password} onChange={set("password")} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setShow(false)}>
                {t("cancel")}
              </button>
              <button
                className="btn btn-primary"
                disabled={!form.mobile || !form.full_name}
                onClick={save}
              >
                {t("save")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
